// --- Dependencies -------------------------------------------------------------------------------
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useAuth from "@/hooks/useAuth";
import useFetch from "@/hooks/useFetch";
import useLang from "@/hooks/useLang";
import useOrganization from "@/hooks/useOrganization";
import usePopup from "@/hooks/usePopup";
import useTranslation from "@/hooks/useTranslation";
import type { TyClient, TyLang, TyMembership } from "@/types";
import { formatDate, readLangValue } from "@/utils";
import Avatar from "@/UI/atoms/Avatar";
import Icon from "@/UI/atoms/Icon";
import StatusChip from "@/UI/atoms/StatusChip";
import HeaderPanel from "@/UI/components/HeaderPanel";
import ChangePasswordPopup from "@/UI/popups/ChangePasswordPopup";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import EditProfilePopup from "@/UI/popups/EditProfilePopup";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
/**
 * The account's own page. **Every role has one**, so it carries no gate and no sidebar tab — it is
 * reached from the chip in the bar, which is where a person looks for themselves.
 *
 * What belongs here is what belongs to the **person**: their details, their password, the companies
 * they can open, and the two preferences that live in this browser. What belongs to a company —
 * its brand, its team, its plan — is the settings, team and subscription screens, which a member
 * never sees at all. Keeping the two apart is why editing your own name is not buried in a screen
 * half the staff cannot reach.
 */
export default function ProfileScreen() {
    const t = useTranslation();
    const navigate = useNavigate();
    const lang = useLang((state) => state.lang) ?? "ar";
    const setLang = useLang((state) => state.setLang);
    const addComp = usePopup((state) => state.addComp);

    const client = useAuth((state) => state.client);
    const memberships = useAuth((state) => state.memberships);
    const updateClient = useAuth((state) => state.updateClient);
    const logout = useAuth((state) => state.logout);
    const organizationId = useOrganization((state) => state.organizationId);
    const setOrganization = useOrganization((state) => state.setOrganization);
    const clearOrganization = useOrganization((state) => state.clearOrganization);

    /**
     * Re-read rather than trusting the stored copy: `isPasswordTemporary` flips as a side effect of
     * a change, and only the backend knows it did.
     */
    const session = useFetch<{ client: TyClient; organizations: TyMembership[] }>("/auth/me");

    useEffect(() => {
        if (session.data?.client) updateClient(session.data.client);
    }, [session.data]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!client) return <p className="profile-screen__state">{t.common.loading}</p>;

    const active = memberships.find((item) => item.organization._id === organizationId) ?? null;
    const others = memberships.filter((item) => item.organization._id !== organizationId);
    const genderLabel = client.gender ? t.profile.genders[client.gender] : t.profile.genders.unset;

    const openEdit = () => addComp({ name: "edit-profile", Component: EditProfilePopup, anim: "scale", details: { onDone: () => session.invoke() } });

    const openPassword = () =>
        addComp({ name: "change-password", Component: ChangePasswordPopup, anim: "scale", details: { onDone: () => session.invoke() } });

    /**
     * Switching company changes what every other screen is looking at, so it is confirmed first and
     * then sends you home — `useFetch` reruns on the organization, so nothing can be left showing
     * the previous company's rows.
     */
    const handleSwitch = (membership: TyMembership) =>
        addComp({
            name: "confirm",
            Component: ConfirmPopup,
            anim: "scale",
            details: {
                title: t.profile.switchTo,
                message: readLangValue(membership.organization.name, lang),
                confirmLabel: t.profile.switchTo,
                danger: false,
                onConfirm: () => {
                    setOrganization(membership.organization._id, membership.role);
                    navigate("/");
                },
            },
        });

    const handleLogout = () => {
        clearOrganization();
        logout();
    };

    return (
        <section className="profile-screen">
            <HeaderPanel
                head={
                    <header className="profile-screen__head">
                        <Avatar name={client.name} picture={client.picture} size={68} bordered />

                        <div className="profile-screen__identity">
                            <h1 className="profile-screen__name">{client.name}</h1>
                            {/* A phone number is left-to-right in every language, always. */}
                            <span className="profile-screen__phone" dir="ltr">
                                {client.phone}
                            </span>
                            <div className="profile-screen__chips">
                                {active && <StatusChip size="sm" tone="accent" label={t.roles[active.role]} />}
                                {client.isPasswordTemporary && <StatusChip size="sm" tone="reserved" label={t.profile.temporaryPassword} />}
                            </div>
                        </div>

                        <div className="profile-screen__head-actions">
                            <button type="button" className="profile-screen__edit" onClick={openEdit}>
                                <Icon name="edit" size={15} />
                                <span>{t.profile.editProfile}</span>
                            </button>
                        </div>
                    </header>
                }
            />

            {/* --- The details themselves ------------------------------------------------------ */}
            <section className="profile-block">
                <div className="profile-block__head">
                    <h2 className="profile-block__title">{t.profile.myProfile}</h2>
                    <p className="profile-block__hint">{t.profile.subtitle}</p>
                </div>

                <div className="profile-facts">
                    <div className="profile-fact">
                        <span className="profile-fact__label">{t.profile.fields.email}</span>
                        <span className="profile-fact__value" dir="ltr">
                            {client.email || t.profile.notSet}
                        </span>
                    </div>
                    <div className="profile-fact">
                        <span className="profile-fact__label">{t.profile.fields.gender}</span>
                        <span className="profile-fact__value">{genderLabel}</span>
                    </div>
                    <div className="profile-fact">
                        <span className="profile-fact__label">{t.profile.fields.birthDate}</span>
                        <span className="profile-fact__value">{client.birthDate ? formatDate(client.birthDate, lang) : t.profile.notSet}</span>
                    </div>
                    <div className="profile-fact">
                        <span className="profile-fact__label">{t.profile.memberSince}</span>
                        <span className="profile-fact__value">{formatDate(client.createdAt, lang)}</span>
                    </div>
                </div>
            </section>

            {/* --- The companies --------------------------------------------------------------- */}
            <section className="profile-block">
                <div className="profile-block__head">
                    <h2 className="profile-block__title">{t.profile.organizations}</h2>
                    <p className="profile-block__hint">{t.profile.organizationsHint}</p>
                </div>

                <div className="profile-orgs">
                    {active && (
                        <article className="profile-org profile-org--active">
                            <Avatar
                                name={readLangValue(active.organization.name, lang)}
                                picture={active.organization.logo}
                                size={38}
                                rounded
                                bordered
                            />
                            <div className="profile-org__text">
                                <span className="profile-org__name">{readLangValue(active.organization.name, lang)}</span>
                                <span className="profile-org__role">{t.roles[active.role]}</span>
                            </div>
                            <StatusChip size="sm" tone="available" label={t.profile.activeOrganization} />
                        </article>
                    )}

                    {others.map((membership) => (
                        <article key={membership.organization._id} className="profile-org">
                            <Avatar
                                name={readLangValue(membership.organization.name, lang)}
                                picture={membership.organization.logo}
                                size={38}
                                rounded
                            />
                            <div className="profile-org__text">
                                <span className="profile-org__name">{readLangValue(membership.organization.name, lang)}</span>
                                <span className="profile-org__role">{t.roles[membership.role]}</span>
                            </div>
                            {/* Only this button switches — the card itself is inert on purpose. */}
                            <button type="button" className="profile-org__switch" onClick={() => handleSwitch(membership)}>
                                {t.profile.switchTo}
                            </button>
                        </article>
                    ))}
                </div>
            </section>

            {/* --- This browser's own settings -------------------------------------------------- */}
            <section className="profile-block">
                <div className="profile-block__head">
                    <h2 className="profile-block__title">{t.profile.preferences}</h2>
                    <p className="profile-block__hint">{t.profile.preferencesHint}</p>
                </div>

                <div className="profile-pref">
                    <span className="profile-pref__label">{t.profile.language}</span>
                    <nav className="profile-pref__choice" role="group">
                        {constants.languages.map((name) => (
                            <button
                                key={name}
                                type="button"
                                className={`profile-pref__option${lang === name ? " profile-pref__option--on" : ""}`}
                                onClick={() => setLang(name as TyLang)}
                                aria-pressed={lang === name}
                            >
                                {constants.languageNames[name]}
                            </button>
                        ))}
                    </nav>
                </div>
            </section>

            {/* Present, and the quietest thing on the page. */}
            {/* --- The password, last: it sits with signing out because both end a session ------ */}
            <section className="profile-block">
                <div className="profile-block__head">
                    <h2 className="profile-block__title">{t.profile.security}</h2>
                    <p className="profile-block__hint">{t.profile.securityHint}</p>
                </div>

                <button type="button" className="profile-action" onClick={openPassword}>
                    <span className="profile-action__mark" aria-hidden="true">
                        <Icon name="settings" size={16} />
                    </span>
                    <span className="profile-action__text">{t.profile.changePassword}</span>
                    <Icon name="chevron" size={14} />
                </button>
            </section>

            <div className="profile-screen__logout-row">
                <button type="button" className="profile-screen__logout" onClick={handleLogout}>
                    <Icon name="logout" size={15} />
                    <span>{t.common.logout}</span>
                </button>
            </div>
        </section>
    );
}
